import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import featureCommentService, { buildCommentTree } from '../../services/featureCommentService';
import { canModerateStaff } from '../../utils/staffPermissions';
import styles from './Features.module.css';

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CommentForm = ({ placeholder, submitLabel, onSubmit, onCancel, submitting }) => {
  const [content, setContent] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!content.trim() || submitting) return;
    const success = await onSubmit(content.trim());
    if (success) {
      setContent('');
    }
  };

  return (
    <form className={styles.commentForm} onSubmit={handleSubmit}>
      <textarea
        className={styles.commentInput}
        value={content}
        onChange={(event) => setContent(event.target.value)}
        placeholder={placeholder}
        rows={3}
        maxLength={2000}
        disabled={submitting}
      />
      <div className={styles.commentFormActions}>
        {onCancel && (
          <button type="button" className={styles.commentBtnSecondary} onClick={onCancel} disabled={submitting}>
            Annuler
          </button>
        )}
        <button type="submit" className={styles.commentBtnPrimary} disabled={submitting || !content.trim()}>
          {submitting ? 'Envoi…' : submitLabel}
        </button>
      </div>
    </form>
  );
};

const CommentItem = ({
  comment,
  onReply,
  onDelete,
  onModerate,
  replyTargetId,
  setReplyTargetId,
  submitting,
}) => {
  const isPlaceholder = comment.status !== 'visible';
  const isReplyOpen = replyTargetId === comment.id;

  return (
    <article className={`${styles.commentItem} ${isPlaceholder ? styles.commentItemMuted : ''}`}>
      <div className={styles.commentHeader}>
        <div className={styles.commentAuthorBlock}>
          {comment.author?.avatar ? (
            <img src={comment.author.avatar} alt="" className={styles.commentAvatar} />
          ) : (
            <div className={styles.commentAvatarPlaceholder} aria-hidden="true">
              {(comment.author?.pseudo || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <strong className={styles.commentAuthor}>{comment.author?.pseudo || 'Joueur'}</strong>
            <time className={styles.commentDate} dateTime={comment.createdAt}>
              {formatDate(comment.createdAt)}
            </time>
            {comment.status === 'hidden' && (
              <span className={styles.commentStatusBadge}>Masqué</span>
            )}
          </div>
        </div>
      </div>

      <p className={styles.commentBody}>{comment.content}</p>

      <div className={styles.commentActions}>
        {comment.permissions?.canReply && (
          <button
            type="button"
            className={styles.commentActionBtn}
            onClick={() => setReplyTargetId(isReplyOpen ? null : comment.id)}
          >
            Répondre
          </button>
        )}
        {comment.permissions?.canDelete && (
          <button
            type="button"
            className={styles.commentActionBtnDanger}
            onClick={() => onDelete(comment)}
          >
            Supprimer
          </button>
        )}
        {comment.permissions?.canModerate && comment.status === 'visible' && (
          <button
            type="button"
            className={styles.commentActionBtnDanger}
            onClick={() => onModerate(comment, 'hidden')}
          >
            Masquer
          </button>
        )}
        {comment.permissions?.canRestore && (
          <button
            type="button"
            className={styles.commentActionBtn}
            onClick={() => onModerate(comment, 'visible')}
          >
            Réafficher
          </button>
        )}
      </div>

      {isReplyOpen && (
        <CommentForm
          placeholder="Votre réponse…"
          submitLabel="Publier la réponse"
          submitting={submitting}
          onCancel={() => setReplyTargetId(null)}
          onSubmit={(content) => onReply(comment.id, content)}
        />
      )}

      {comment.replies?.length > 0 && (
        <div className={styles.commentReplies}>
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onDelete={onDelete}
              onModerate={onModerate}
              replyTargetId={replyTargetId}
              setReplyTargetId={setReplyTargetId}
              submitting={submitting}
            />
          ))}
        </div>
      )}
    </article>
  );
};

const FeatureComments = ({ featureSlug }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [permissions, setPermissions] = useState({ canComment: false, canModerate: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTargetId, setReplyTargetId] = useState(null);

  const loadComments = useCallback(async () => {
    try {
      setError('');
      const data = await featureCommentService.getComments(featureSlug);
      setComments(Array.isArray(data?.comments) ? data.comments : []);
      setPermissions(data?.permissions || { canComment: false, canModerate: false });
    } catch (err) {
      setError('Impossible de charger les commentaires.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureSlug]);

  useEffect(() => {
    setLoading(true);
    loadComments();
  }, [loadComments]);

  const commentTree = useMemo(() => buildCommentTree(comments), [comments]);

  const handleCreate = async (content, parentId = null) => {
    setSubmitting(true);
    try {
      await featureCommentService.createComment(featureSlug, { content, parentId });
      setReplyTargetId(null);
      await loadComments();
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la publication.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (comment) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    setSubmitting(true);
    try {
      await featureCommentService.deleteComment(comment.id);
      await loadComments();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModerate = async (comment, status) => {
    setSubmitting(true);
    try {
      await featureCommentService.moderateComment(comment.id, status);
      await loadComments();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la modération.');
    } finally {
      setSubmitting(false);
    }
  };

  const canModerate = canModerateStaff(user) || permissions.canModerate;

  return (
    <section className={styles.commentsSection} aria-label="Commentaires">
      <h2 className={styles.commentsTitle}>Commentaires</h2>
      <p className={styles.commentsIntro}>
        Posez vos questions, partagez vos retours ou signalez un problème. Les modérateurs peuvent masquer
        les messages inappropriés.
      </p>

      {loading && <p className={styles.commentsMessage}>Chargement des commentaires…</p>}
      {error && <p className={styles.commentsError}>{error}</p>}

      {!loading && commentTree.length === 0 && (
        <p className={styles.commentsEmpty}>Aucun commentaire pour le moment. Soyez le premier à réagir !</p>
      )}

      {!loading && commentTree.length > 0 && (
        <div className={styles.commentList}>
          {commentTree.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onReply={handleCreate}
              onDelete={handleDelete}
              onModerate={handleModerate}
              replyTargetId={replyTargetId}
              setReplyTargetId={setReplyTargetId}
              submitting={submitting}
            />
          ))}
        </div>
      )}

      {permissions.canComment ? (
        <div className={styles.commentComposer}>
          <h3 className={styles.commentComposerTitle}>Ajouter un commentaire</h3>
          <CommentForm
            placeholder="Votre message…"
            submitLabel="Publier"
            submitting={submitting}
            onSubmit={(content) => handleCreate(content)}
          />
        </div>
      ) : (
        <p className={styles.commentsLoginHint}>
          <Link to="/login">Connectez-vous</Link> pour participer à la discussion.
        </p>
      )}

      {canModerate && (
        <p className={styles.commentsModeratorHint}>
          Vous voyez les commentaires masqués avec leur contenu d&apos;origine et pouvez les réafficher.
        </p>
      )}
    </section>
  );
};

export default FeatureComments;
